import {
  useState,
  useEffect,
  useContext,
  type ReactNode,
} from 'react';
import { AuthContext, type AuthContextType } from '@/context/authContext';
import type { UserType } from '@/types/users';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [externalToken, setExternalToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const checkAuth = async () => {
  try {
    const tokenLS = localStorage.getItem('token');
    const userDataLS = localStorage.getItem('user');
    const externalTokenLS = localStorage.getItem('externalToken');

    if (tokenLS && userDataLS) {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/auth/verify`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${tokenLS}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const parsedUser: UserType = JSON.parse(userDataLS);
        setUser(parsedUser);
        setToken(tokenLS);
        setExternalToken(externalTokenLS);
        setIsLoggedIn(true);
      } else {
        localStorage.clear();
        setUser(null);
        setToken(null);
        setExternalToken(null);
        setIsLoggedIn(false);
      }
    } else {
      setUser(null);
      setToken(null);
      setExternalToken(null);
      
      setIsLoggedIn(false);
    }
  } catch (error) {
    console.error('Eroare la verificarea autentificării:', error);
    localStorage.clear();
    setUser(null);
    setToken(null);
    setExternalToken(null);
    setIsLoggedIn(false);
  } finally {
    setIsLoading(false);
  }
};

useEffect(() => {
  checkAuth();
}, []);


  const login: AuthContextType['login'] = (
    userData,
    tokenReceived,
    externalTokenReceived
  ) => {
    localStorage.setItem('token', tokenReceived);
    localStorage.setItem('user', JSON.stringify(userData));
    if (externalTokenReceived) {
      localStorage.setItem('externalToken', externalTokenReceived);
      setExternalToken(externalTokenReceived);
    } else {
      localStorage.removeItem('externalToken');
      setExternalToken(null);
    }
    setUser(userData);
    setToken(tokenReceived);
    setIsLoggedIn(true);
  };

  const logout: AuthContextType['logout'] = async () => {
    try {
      const tokenLS = localStorage.getItem('token');
      if (tokenLS) {
        await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenLS}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Eroare la logout pe backend:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('externalToken');
      setUser(null);
      setToken(null);
      setExternalToken(null);
      setIsLoggedIn(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, token, externalToken, isLoggedIn, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
